import { Content } from 'src/contexts/ContentContext';
import { ContentId } from 'src/utils/constants/contentId';

import { MobilePresetChip, PresetPills } from './PresetPills';
import { PriceMovementGraph } from './PriceMovementGraph';
import { RecommendationTable } from './RecommendationTable';
import { SellThroughGraph } from './SellThroughGraph';
import { StrategyVideoBanner } from './StrategyCalculatorPanel.components';
import * as styles from './StrategyCalculatorPanel.css';
import { useStrategyCalculator } from './StrategyCalculatorPanel.hooks';
import { StrategyCalculatorPanelProps } from './StrategyCalculatorPanel.types';

export const StrategyCalculatorContent = ({
  onClose,
  showCloseButton,
  panelClassName,
  isMobile,
  eventDateStr,
  onRecommendationChange,
}: {
  onClose: () => void;
  showCloseButton: boolean;
  panelClassName: string;
  isMobile: boolean;
  eventDateStr?: string | null;
  onRecommendationChange?: (strategyId: string) => void;
}) => {
  const {
    demandPreset,
    setDemandPreset,
    sellThroughPreset,
    setSellThroughPreset,
    demandOptions,
    sellThroughOptions,
    strategyRows,
    recommendedLabel,
    priceCurveData,
    salesCurveData,
    eventDate,
    daysToEvent,
    panelTitle,
    closeAriaLabel,
    panelDescription,
    priceMovementTitle,
    eventDemandLabel,
    sellThroughTitle,
    lastMinuteSalesLabel,
  } = useStrategyCalculator({
    eventDateStr,
    onRecommendationChange,
  });

  const PresetComponent = isMobile ? MobilePresetChip : PresetPills;

  return (
    <div className={panelClassName}>
      <div className={styles.headerSection}>
        <div className={styles.titleRow}>
          <span className={styles.title}>{panelTitle}</span>
          {showCloseButton && (
            <button
              className={styles.closeLink}
              onClick={onClose}
              aria-label={closeAriaLabel}
            >
              <Content id={ContentId.Close} />
            </button>
          )}
        </div>
        <p className={styles.description}>{panelDescription}</p>
        <StrategyVideoBanner />
      </div>
      <div className={styles.content}>
        <div className={styles.section}>
          <span className={styles.sectionTitle}>{priceMovementTitle}</span>
          <div className={styles.chartWithPills}>
            <div className={styles.chartPillsOverlay}>
              <PresetComponent
                label={eventDemandLabel}
                options={demandOptions}
                value={demandPreset}
                onChange={setDemandPreset}
              />
            </div>
            <PriceMovementGraph
              data={priceCurveData}
              eventDate={eventDate}
              daysToEvent={daysToEvent}
            />
          </div>
        </div>

        <div className={styles.section}>
          <span className={styles.sectionTitle}>{sellThroughTitle}</span>
          <div className={styles.chartWithPills}>
            <div className={styles.chartPillsOverlay}>
              <PresetComponent
                label={lastMinuteSalesLabel}
                options={sellThroughOptions}
                value={sellThroughPreset}
                onChange={setSellThroughPreset}
              />
            </div>
            <SellThroughGraph
              data={salesCurveData}
              eventDate={eventDate}
              daysToEvent={daysToEvent}
            />
          </div>
        </div>

        <RecommendationTable
          rows={strategyRows}
          recommendedLabel={recommendedLabel}
          isMobile={isMobile}
        />
      </div>
    </div>
  );
};

export const StrategyCalculatorPanel = ({
  onClose,
  eventDateStr,
  onRecommendationChange,
}: StrategyCalculatorPanelProps) => {
  return (
    <StrategyCalculatorContent
      onClose={onClose}
      showCloseButton
      panelClassName={styles.panel}
      isMobile={false}
      eventDateStr={eventDateStr}
      onRecommendationChange={onRecommendationChange}
    />
  );
};
